<template>
	<TheTable
		v-if="localTableData"
		:tableData="localTableData"
		:loading="loading"
		:defaultSort="sort"
		:showMaxRows="14"
		@rowClick="goToDetailPage"
		@rowClickNewTab="goToDetailPageNewTab"
		:mobileBox="mobileBox"
	>
		<template #box>
			<BoxCase v-for="item in localTableData" :key="item.hid" :item="item" />
		</template>

		<el-table-column prop="state" label="Status" width="120" sortable>
			<template #default="scope">
				<div class="has-status" :class="`has-status--${$getStateColor('case', scope.row.state)}`">
					{{ $getStateFullLabel('case', scope.row.state) }}
				</div>
			</template>
		</el-table-column>
		<el-table-column prop="hid" label="ID" width="80" sortable />
		<el-table-column prop="createdDate" label="Datum" width="100" sortable>
			<template #default="scope">
				<span>{{ $formatDate(scope.row.createdDate) }}</span>
			</template>
		</el-table-column>
		<el-table-column prop="title" label="Titel" min-width="120" sortable :sort-method="(a, b) => $sortAlphabetically(a, b, 'title')" />
		<el-table-column prop="medium" label="Medium" width="130" sortable>
			<template #default="scope">
				<span v-if="scope.row.mediums">{{ scope.row.mediums.map((medium) => medium.name).join(', ') }}</span>
				<span v-else>-</span>
			</template>
		</el-table-column>
		<el-table-column prop="" label="Anhang" width="100" sortable :sort-method="$sortAttachments">
			<template #default="scope">
				<el-icon v-if="getCaseFiles(scope.row)?.length > 0 || scope.row.links.length > 0"><Paperclip /></el-icon>
				<span v-else>-</span>
			</template>
		</el-table-column>
		<el-table-column prop="" label="Kategorie" width="130" sortable>
			<template #default="scope">
				<span>{{ $recursivelyFindCategoryName(scope.row.category.number) }}</span>
			</template>
		</el-table-column>
		<el-table-column prop="complaints.length" label="Beschw." width="90" sortable />
		<el-table-column prop="" label="Stn.-Anzahl" width="110" sortable>
			<template #default="scope">
				<span>{{ `${answeredStatementsCount(scope.row.statements)}/${scope.row.statements.length}` }}</span>
			</template>
		</el-table-column>
		<el-table-column
			prop=""
			label="Stn. bis"
			width="90"
			sortable
			:sort-method="(a, b) => $sortDates(findLastValidStatementDate(a.statements), findLastValidStatementDate(b.statements))"
		>
			<template #default="scope">
				<span v-if="scope.row.statements.length > 0">{{ $formatDate(findLastValidStatementDate(scope.row.statements)) }}</span>
				<span v-else>-</span>
			</template>
		</el-table-column>
		<el-table-column prop="" label="WR-Entsch. bis" width="130" sortable :sort-method="(a, b) => $sortDates(a.voteEndDate, b.voteEndDate)">
			<template #default="scope">
				<span v-if="scope.row.voteEndDate">{{ $formatDate(scope.row.voteEndDate) }}</span>
				<span v-else>-</span>
			</template>
		</el-table-column>
		<el-table-column prop="publishedDate" label="Freigabe" width="100" sortable>
			<template #default="scope">
				<span v-if="scope.row.publishedDate">Ja</span>
				<span v-else>Nein</span>
			</template>
		</el-table-column>
	</TheTable>

	<TablePagination :currentLength="tableData.length" :totalMaxVal="totalCount" @loadNextPage="$emit('loadNextPage')" />
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { Paperclip } from '@element-plus/icons-vue';
import TheTable from '../../components/TheTable.vue';
import { authUser } from '../../store';
import { CaseFilters, CaseResponse, StatementResponse } from '../../types/types';
import TablePagination from './TablePagination.vue';
import TheBox from '../TheBox.vue';
import TheButton from '../TheButton.vue';
import { TableSort, CaseState } from '../../types/types';
import BoxCase from '../boxes/BoxCase.vue';
import { getCaseFiles } from '../../utils/fileHandling/files';

const caseStateOrder: { [key in CaseState]: number } = {
	[CaseState.NoStatus]: 0,
	[CaseState.ExaminationSmallSenate]: 1,
	[CaseState.ExaminationSmallSenateRunning]: 2,
	[CaseState.Statements]: 3,
	[CaseState.ExaminationWerberat]: 4,
	[CaseState.ExaminationWerberatRunning]: 5,
	[CaseState.DecisionSensitization]: 6,
	[CaseState.DecisionNoReason]: 7,
	[CaseState.DecisionStop]: 8,
	[CaseState.ImmediateSubjectWithdrawal]: 9,
	[CaseState.NotResponsible]: 10,
	[CaseState.ProcedureNotPossible]: 11,
	[CaseState.Unfounded]: 12,
};

export default defineComponent({
	name: 'CasesTable',
	setup() {
		return { user: authUser };
	},
	data() {
		return {
			localTableData: [...this.tableData], // Local copy of the table data (for sorting)
		};
	},
	props: {
		tableData: {
			type: Array as PropType<CaseResponse[]>,
			default: [],
			required: true,
		},
		loading: {
			type: Boolean,
			default: false,
		},
		totalCount: {
			type: Number,
			default: 0,
		},
		mobileBox: {
			type: Boolean,
			default: false,
		},
		sort: {
			type: Object as PropType<TableSort>,
			default: { prop: 'createdDate', order: 'descending' },
		},
	},
	components: {
		Paperclip,
		TheTable,
		TablePagination,
		TheBox,
		TheButton,
		BoxCase,
	},
	emits: ['loadNextPage'],
	methods: {
		goToDetailPage(row) {
			this.$router.push({ name: 'FallDetail', params: { id: String(row.id) } });
		},
		goToDetailPageNewTab(row) {
			window.open(this.$router.resolve({ name: 'FallDetail', params: { id: String(row.id) } }).href, '_blank');
		},
		answeredStatementsCount(statements: StatementResponse[]) {
			return statements.filter((s) => s.answer).length;
		},
		findLastValidStatementDate(statements: StatementResponse[]) {
			let lastValidDate = '';
			statements.forEach((s) => {
				if (s.lastValidDate && s.lastValidDate > lastValidDate) {
					lastValidDate = s.lastValidDate;
				} else if (s.firstValidDate && s.firstValidDate > lastValidDate) {
					lastValidDate = s.firstValidDate;
				}
			});
			return lastValidDate;
		},
		sortByState(arr, ascending: boolean = true) {
			return arr.sort((a, b) => {
				const stateA = caseStateOrder[a.state];
				const stateB = caseStateOrder[b.state];

				if (ascending) {
					return stateA - stateB;
				} else {
					return stateB - stateA;
				}
			});
		},
		sortByTitle(arr, ascending: boolean = true) {
			return arr.sort((a, b) => {
				const titleA = a.title.toLowerCase();
				const titleB = b.title.toLowerCase();
				if (ascending) {
					return titleA.localeCompare(titleB);
				} else {
					return titleB.localeCompare(titleA);
				}
			});
		},
		sortByDate(arr, ascending: boolean = true) {
			return arr.sort((a, b) => {
				const dateA = a.voteEndDate ? new Date(a.voteEndDate).getTime() : Infinity;
				const dateB = b.voteEndDate ? new Date(b.voteEndDate).getTime() : Infinity;

				return ascending ? dateA - dateB : dateB - dateA;
			});
		},
		sortTableData(newSort) {
			if (this.mobileBox && newSort?.prop) {
				const asc = newSort.order === 'ascending';
				switch (newSort.prop) {
					case 'state':
						this.localTableData = this.sortByState(this.localTableData, asc);
						break;
					case 'title':
						this.localTableData = this.sortByTitle(this.localTableData, asc);
						break;
					case 'voteEndDate':
						this.localTableData = this.sortByDate(this.localTableData, asc);
						break;

					default:
						break;
				}
			}
		},
		getCaseFiles,
	},
	watch: {
		tableData(newData) {
			// Update local copy when tableData prop changes
			this.localTableData = [...newData];

			this.sortTableData(this.sort);
		},
		sort(newSort) {
			this.sortTableData(newSort);
		},
	},
	mounted() {
		this.sortTableData(this.sort);
	},
});
</script>
